import { useTranslation } from '../i18n';
import { SourceFavicon } from '../primitives';
import { formatRelative } from '../utils/relativeTime';
import type { WorkSummary } from '../api/types';
import { isAdmin, useMe } from '../auth/useMe';
import { MirrorStateBadge } from './MirrorStateBadge';
import { COL, ROW_FRAME } from './WorkRowLayout';
import { workUrl, withBase } from '../paths';
import { PublicVisibilityBadge } from '../primitives/PublicVisibilityBadge';

const ISO_DATE_FORMAT = new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: 'numeric' });

function formatUpstreamTooltip(
    createdAt: string | null | undefined,
    updatedAt: string | null | undefined,
): string | undefined {
    const created = formatIsoDate(createdAt);
    const updated = formatIsoDate(updatedAt);
    if (created && updated) return `Created ${created} · Updated ${updated}`;
    if (updated) return `Updated ${updated}`;
    if (created) return `Created ${created}`;
    return undefined;
}

function formatIsoDate(value: string | null | undefined): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return ISO_DATE_FORMAT.format(date);
}

export function WorkRow({ pkg }: { pkg: WorkSummary }) {
    const t = useTranslation();
    const me = useMe();
    const viewerIsAdmin = me.status === 'authenticated' && isAdmin(me.user);
    // Mirror toCardModel's predicate: Mirror/Upstream both surface a one-click Studio install,
    // gated on Installable (ADR-0026) so tools/other never show an install affordance.
    const canInstall = pkg.installable && (pkg.install.kind === 'Mirror' || pkg.install.kind === 'Upstream');
    const extraScouts = Math.max(0, pkg.sightings.scoutSlugs.length - 1);

    // Server-projected per-user marker (ADR-0015); null/false for anonymous.
    const claimedByMe = pkg.claimedByMe ?? false;
    const lifecycleStatus = viewerIsAdmin && pkg.status !== 'Published' ? pkg.status : undefined;

    // Left-edge 3px bar: claim > Suppressed > transparent.
    // Post ADR-0022 correction (A.6): Discovered Works don't exist -
    // a Sighting either evaluates and triggers Converge (Work
    // materializes Published) or gets RejectedBy* (no Work row at all).
    const leftBarColor = claimedByMe
        ? 'border-l-[3px] border-l-accent'
        : lifecycleStatus === 'Suppressed'
            ? 'border-l-[3px] border-l-state-failed border-dashed'
            : 'border-l-[3px] border-l-transparent';

    return (
        <a
            href={withBase(workUrl(pkg))}
            class={`${ROW_FRAME} ${leftBarColor} group hover:bg-accent-glow`}
        >
            <div class={COL.source}>
                <SourceFavicon source={pkg.source} size={16} title={pkg.source ?? undefined} />
                {extraScouts > 0 && (
                    <span class="text-[10px] text-fg-muted">+{extraScouts}</span>
                )}
            </div>

            <div class={`${COL.name} min-w-0`}>
                <span class="block truncate font-display text-[17px] italic font-normal text-fg-primary transition-colors group-hover:text-accent">
                    {pkg.name ?? t('catalogRow.unnamed')}
                </span>
                <PublicVisibilityBadge publiclyListed={pkg.publiclyListed} />
            </div>

            <span class={COL.author}>{pkg.author ?? ''}</span>

            <span class={`${COL.kind} font-mono text-[11.5px] lowercase`}>
                {t(`catalogKind.${pkg.kind.toLowerCase()}`)}
            </span>

            <div class={COL.install}>
                <RowActionPill
                    canInstall={canInstall}
                    label={canInstall ? t('row.actionInstall') : t('row.actionVisit')}
                />
            </div>

            <div class={COL.mirror}>
                <MirrorStateBadge summary={pkg.mirrorSummary} />
            </div>

            <span
                class={`${COL.last} [font-feature-settings:'tnum']`}
                title={formatUpstreamTooltip(pkg.upstream.createdAt, pkg.upstream.updatedAt)}
            >
                {formatRelative(
                    pkg.upstream.updatedAt ?? pkg.sightings.lastChangedAt ?? pkg.lastSeenAt,
                    t,
                )}
            </span>
        </a>
    );
}

function RowActionPill({ canInstall, label }: { canInstall: boolean; label: string }) {
    if (canInstall) {
        return (
            <span class="inline-flex items-center gap-1 rounded-full border border-accent bg-accent px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-surface-base transition-colors group-hover:border-fg-primary group-hover:bg-fg-primary">
                <DownloadIcon />
                {label}
            </span>
        );
    }
    return (
        <span class="inline-flex items-center gap-1 rounded-full border border-rule-strong px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-secondary transition-colors group-hover:border-fg-muted group-hover:text-fg-primary">
            <ExternalIcon />
            {label}
        </span>
    );
}

function DownloadIcon() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
    );
}

function ExternalIcon() {
    return (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
    );
}
